import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Calendar, Layers, Loader2, MapPin, Medal, Trophy } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import Layout from "../../../components/Layout";
import api, { formatApiError } from "../../../lib/api";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";

function PodiumIcon({ rank }) {
  const colors = {
    1: "bg-amber-400 text-white ring-amber-200",
    2: "bg-slate-300 text-slate-800 ring-slate-200",
    3: "bg-orange-400 text-white ring-orange-200",
  };
  const cls = colors[rank] || "bg-slate-100 text-slate-600 ring-slate-200";
  return (
    <div className={`h-12 w-12 rounded-full ring-4 ${cls} flex items-center justify-center font-display font-bold text-xl flex-shrink-0`}>
      {rank}
    </div>
  );
}

export default function Classifica() {
  const [mode, setMode] = useState("single"); // "single" | "combined"
  const [competitions, setCompetitions] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Combined mode
  const [selectedIds, setSelectedIds] = useState([]);
  const [combined, setCombined] = useState(null);
  const [loadingCombined, setLoadingCombined] = useState(false);

  useEffect(() => {
    api.get("/competitions")
      .then((r) => setCompetitions(r.data.filter((c) => c.sent)))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  useEffect(() => {
    if (!selectedId) { setSummary(null); return; }
    setLoadingSummary(true);
    api.get(`/competitions/${selectedId}/judging-summary`)
      .then((r) => setSummary(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoadingSummary(false));
  }, [selectedId]);

  const ranking = useMemo(() => {
    if (!summary) return [];
    const list = [...summary.athletes].filter((a) => a.grand_total != null);
    list.sort((a, b) => b.grand_total - a.grand_total);
    let lastTotal = null, lastRank = 0;
    return list.map((a, i) => {
      const rank = (lastTotal != null && a.grand_total === lastTotal) ? lastRank : i + 1;
      lastTotal = a.grand_total; lastRank = rank;
      return { ...a, rank };
    });
  }, [summary]);

  const combinedRanking = useMemo(() => {
    if (!combined) return [];
    const list = combined.rows.filter((r) => r.grand_total != null);
    let lastTotal = null, lastRank = 0;
    return list.map((r, i) => {
      const rank = (lastTotal != null && r.grand_total === lastTotal) ? lastRank : i + 1;
      lastTotal = r.grand_total; lastRank = rank;
      return { ...r, rank };
    });
  }, [combined]);

  const selectedComp = competitions.find((c) => c.id === selectedId);

  const toggleId = (id) => {
    setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  };

  const runCombined = async () => {
    if (selectedIds.length < 2) {
      toast.error("Seleziona almeno 2 competizioni");
      return;
    }
    setLoadingCombined(true);
    try {
      const { data } = await api.post("/results/combined-ranking", { competition_ids: selectedIds });
      setCombined(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoadingCombined(false);
    }
  };

  return (
    <Layout>
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="back-to-admin">
        <ArrowLeft className="h-4 w-4" /> Torna al pannello
      </Link>

      <div className="mb-8 flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center">
          <Medal className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-blue-700 font-bold">Tools Admin</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-1">Classifica / Ranking</h1>
        </div>
      </div>

      {/* Mode toggle */}
      <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-2 mb-4 inline-flex gap-1" data-testid="ranking-mode-toggle">
        <button
          onClick={() => setMode("single")}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${mode === "single" ? "bg-blue-700 text-white" : "text-slate-700 hover:bg-slate-50"}`}
          data-testid="mode-single"
        >
          Singola competizione
        </button>
        <button
          onClick={() => setMode("combined")}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition inline-flex items-center gap-2 ${mode === "combined" ? "bg-blue-700 text-white" : "text-slate-700 hover:bg-slate-50"}`}
          data-testid="mode-combined"
        >
          <Layers className="h-3.5 w-3.5" />
          Combina più competizioni
        </button>
      </div>

      {mode === "single" ? (
        <>
          <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-6 mb-6">
            <label className="text-sm font-semibold text-slate-700">Seleziona competizione</label>
            <Select value={selectedId} onValueChange={setSelectedId}>
              <SelectTrigger className="mt-2 h-11 max-w-2xl" data-testid="classifica-select">
                <SelectValue placeholder={competitions.length ? "Scegli una competizione…" : "Nessuna competizione inviata"} />
              </SelectTrigger>
              <SelectContent>
                {competitions.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`classifica-option-${c.id}`}>
                    {c.name} — {c.location} — {new Date(c.date).toLocaleDateString("it-IT")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loadingSummary ? (
            <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-10 text-center">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
            </div>
          ) : !summary ? (
            <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-10 text-center text-slate-500 text-sm">
              {competitions.length === 0
                ? "Nessuna competizione inviata. Le classifiche appaiono qui dopo l'invio."
                : "Seleziona una competizione per vedere la classifica."}
            </div>
          ) : ranking.length === 0 ? (
            <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-10 text-center text-slate-500 text-sm">
              Nessun voto ricevuto: la classifica sarà disponibile dopo le valutazioni dei giudici.
            </div>
          ) : (
            <div className="bg-white rounded-2xl ring-1 ring-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex flex-wrap items-center gap-x-5 gap-y-1">
                <Trophy className="h-5 w-5 text-amber-600" />
                <h2 className="font-display text-lg font-semibold text-slate-900">{selectedComp?.name}</h2>
                <span className="text-sm text-slate-500 inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{selectedComp?.location}</span>
                <span className="text-sm text-slate-500 inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{new Date(selectedComp?.date).toLocaleDateString("it-IT")}</span>
                <Badge className="ml-auto bg-blue-100 text-blue-700 hover:bg-blue-100">{ranking.length} atleti classificati</Badge>
              </div>
              <ul className="divide-y divide-slate-100">
                {ranking.map((a) => (
                  <li key={a.id} className={`px-6 py-4 flex items-center gap-4 ${a.rank <= 3 ? "bg-amber-50/30" : ""}`} data-testid={`rank-row-${a.id}`}>
                    <PodiumIcon rank={a.rank} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-3">
                        <span className="font-mono font-bold text-blue-700">{a.number}</span>
                        <span className="font-display font-semibold text-slate-900 text-lg truncate">{a.name}</span>
                      </div>
                      <div className="text-xs text-slate-500 uppercase tracking-wider mt-0.5">{a.type}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Totale</div>
                      <div className="font-display font-bold text-2xl text-blue-700 tabular-nums">{a.grand_total}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-6 mb-6">
            <label className="text-sm font-semibold text-slate-700">Seleziona 2 o più competizioni da combinare</label>
            <div className="mt-3 max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 divide-y divide-slate-200" data-testid="combined-list">
              {competitions.length === 0 ? (
                <p className="text-sm text-slate-500 p-4">Nessuna competizione inviata.</p>
              ) : (
                competitions.map((c) => {
                  const checked = selectedIds.includes(c.id);
                  return (
                    <label
                      key={c.id}
                      className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition ${checked ? "bg-blue-50" : "hover:bg-white"}`}
                      data-testid={`combined-option-${c.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleId(c.id)}
                        className="h-4 w-4 rounded border-slate-300 text-blue-700"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 text-sm">{c.name}</div>
                        <div className="text-xs text-slate-500">
                          {c.type === "latin" ? "Latin Style · " : "Singolo · "}
                          {c.location} · {new Date(c.date).toLocaleDateString("it-IT")}
                        </div>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
            <div className="mt-4 flex flex-wrap gap-3 items-center">
              <Button
                onClick={runCombined}
                disabled={selectedIds.length < 2 || loadingCombined}
                className="bg-blue-700 hover:bg-blue-800 text-white"
                data-testid="combined-run"
              >
                {loadingCombined ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trophy className="h-4 w-4 mr-2" />}
                Genera classifica combinata
              </Button>
              <span className="text-xs text-slate-500">{selectedIds.length} selezionat{selectedIds.length === 1 ? "a" : "e"}</span>
            </div>
          </div>

          {combined && (
            <div className="bg-white rounded-2xl ring-1 ring-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100">
                <div className="flex items-center gap-3 flex-wrap">
                  <Trophy className="h-5 w-5 text-amber-600" />
                  <h2 className="font-display text-lg font-semibold text-slate-900">Classifica combinata</h2>
                  <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 ml-auto">
                    {combinedRanking.length} atleti su {combined.competitions.length} competizioni
                  </Badge>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {combined.competitions.map((c) => (
                    <Badge key={c.id} variant="outline" className="text-xs border-slate-300">
                      {c.name} · {new Date(c.date).toLocaleDateString("it-IT")}
                    </Badge>
                  ))}
                </div>
              </div>
              {combinedRanking.length === 0 ? (
                <div className="p-10 text-center text-slate-500 text-sm">Nessun atleta con voti finalizzati.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {combinedRanking.map((r) => (
                    <li key={`${r.competition_id}-${r.athlete_id}`} className={`px-6 py-4 flex items-center gap-4 ${r.rank <= 3 ? "bg-amber-50/30" : ""}`} data-testid={`combined-row-${r.athlete_id}`}>
                      <PodiumIcon rank={r.rank} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-3 flex-wrap">
                          <span className="font-mono font-bold text-blue-700">{r.number}</span>
                          <span className="font-display font-semibold text-slate-900 text-lg truncate">{r.name}</span>
                        </div>
                        <div className="text-xs text-slate-500 uppercase tracking-wider mt-0.5 inline-flex items-center gap-2 flex-wrap">
                          <span>{r.type}</span>
                          {r.classe && <span>· Cl. {r.classe}</span>}
                          {r.fascia_eta && <span>· {r.fascia_eta}</span>}
                          <span className="text-slate-400">·</span>
                          <span className="text-slate-600 normal-case tracking-normal">{r.competition_name}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Totale</div>
                        <div className="font-display font-bold text-2xl text-blue-700 tabular-nums">{r.grand_total}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </Layout>
  );
}
